/**
 * MontaTech v2 - SettingsModal Component
 * 
 * Modal de configurações com tabs de Perfil e Opções
 */

import { useState } from 'react';
import { X, Plus, Trash2, Cloud, HardDrive } from 'lucide-react';
import type { MontaTechProfile, SettingsTab } from '../types';
import { 
  ROTATION_OPTIONS, 
  PROFILE_BOOLEAN_OPTIONS, 
  MARGIN_FIELDS,
  BLOCKS_PER_ROW_OPTIONS,
  micronsToMm, 
  mmToMicrons 
} from '../constants';

interface SettingsModalProps {
  isOpen: boolean;
  activeTab: SettingsTab;
  profiles: MontaTechProfile[];
  activeProfiles: MontaTechProfile[];
  apiProfiles: MontaTechProfile[];
  localProfiles: MontaTechProfile[];
  selectedProfileName: string;
  editingProfile: MontaTechProfile;
  isLocalProfile: boolean;
  isLoading: boolean;
  onClose: () => void;
  onTabChange: (tab: SettingsTab) => void;
  onSelectProfile: (name: string) => void;
  onUpdateProfile: (updates: Partial<MontaTechProfile>) => void;
  onSaveProfile: (name?: string) => void;
  onDeleteProfile: (name: string) => void;
}

export function SettingsModal({
  isOpen,
  activeTab,
  profiles,
  activeProfiles,
  apiProfiles,
  localProfiles,
  selectedProfileName,
  editingProfile,
  isLocalProfile,
  isLoading,
  onClose,
  onTabChange,
  onSelectProfile,
  onUpdateProfile,
  onSaveProfile,
  onDeleteProfile
}: SettingsModalProps) {
  const [newProfileName, setNewProfileName] = useState('');

  if (!isOpen) return null;

  const handleSave = () => {
    onSaveProfile(newProfileName || undefined);
    setNewProfileName('');
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50">
      <div className="w-full sm:w-[420px] bg-navy-800 sm:rounded-xl rounded-t-xl border-t sm:border border-navy-700 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-navy-700">
          <span className="text-sm font-medium text-gray-200">Configurações</span>
          <button 
            onClick={onClose} 
            className="p-1 hover:bg-navy-700 rounded transition-colors"
          >
            <X className="w-4 h-4 text-gray-400" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-navy-700">
          <button
            onClick={() => onTabChange('profile')}
            className={`flex-1 py-2.5 text-sm font-medium transition-colors ${
              activeTab === 'profile' 
                ? 'text-blue-400 border-b-2 border-blue-400' 
                : 'text-gray-400 hover:text-gray-200'
            }`}
          >
            Perfil
          </button>
          <button
            onClick={() => onTabChange('options')}
            className={`flex-1 py-2.5 text-sm font-medium transition-colors ${
              activeTab === 'options' 
                ? 'text-blue-400 border-b-2 border-blue-400' 
                : 'text-gray-400 hover:text-gray-200'
            }`}
          >
            Opções
          </button>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {activeTab === 'profile' && (
            <ProfileTab
              profiles={profiles}
              activeProfiles={activeProfiles}
              apiProfiles={apiProfiles}
              localProfiles={localProfiles}
              selectedProfileName={selectedProfileName}
              editingProfile={editingProfile}
              isLocalProfile={isLocalProfile}
              isLoading={isLoading}
              newProfileName={newProfileName}
              onNewProfileNameChange={setNewProfileName}
              onSelectProfile={onSelectProfile}
              onUpdateProfile={onUpdateProfile}
              onSaveProfile={handleSave}
              onDeleteProfile={onDeleteProfile}
            />
          )}

          {activeTab === 'options' && (
            <OptionsTab
              editingProfile={editingProfile}
              isLocalProfile={isLocalProfile}
              onUpdateProfile={onUpdateProfile}
            />
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-navy-700">
          <button
            onClick={onClose}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 rounded-lg transition-colors"
          >
            Aplicar
          </button>
        </div>
      </div>
    </div>
  );
}

// ==================== PROFILE TAB ====================

interface ProfileTabProps {
  profiles: MontaTechProfile[];
  activeProfiles: MontaTechProfile[];
  apiProfiles: MontaTechProfile[];
  localProfiles: MontaTechProfile[];
  selectedProfileName: string;
  editingProfile: MontaTechProfile;
  isLocalProfile: boolean;
  isLoading: boolean;
  newProfileName: string;
  onNewProfileNameChange: (name: string) => void;
  onSelectProfile: (name: string) => void;
  onUpdateProfile: (updates: Partial<MontaTechProfile>) => void;
  onSaveProfile: () => void;
  onDeleteProfile: (name: string) => void;
}

function ProfileTab({
  activeProfiles,
  apiProfiles,
  localProfiles,
  selectedProfileName,
  editingProfile,
  isLocalProfile,
  isLoading,
  newProfileName,
  onNewProfileNameChange,
  onSelectProfile,
  onUpdateProfile,
  onSaveProfile,
  onDeleteProfile
}: ProfileTabProps) {
  return (
    <>
      {/* Seletor de Perfil */}
      <div>
        <label className="block text-xs text-gray-400 mb-2 uppercase tracking-wide">
          Perfil
        </label>
        <select
          value={selectedProfileName}
          onChange={e => onSelectProfile(e.target.value)}
          className="w-full bg-navy-900 border border-navy-600 rounded-lg px-3 py-2 text-sm text-gray-200 focus:border-blue-500 focus:outline-none"
        >
          {isLoading && <option>Carregando...</option>}
          {apiProfiles.some(p => p.enabled !== false) && (
            <optgroup label="☁️ Perfis do Servidor">
              {apiProfiles.filter(p => p.enabled !== false).map(p => (
                <option key={`api-${p.name}`} value={p.name}>{p.name}</option>
              ))}
            </optgroup>
          )}
          {localProfiles.some(p => p.enabled !== false) && (
            <optgroup label="💾 Perfis Locais">
              {localProfiles.filter(p => p.enabled !== false).map(p => (
                <option key={`local-${p.name}`} value={p.name}>{p.name}</option>
              ))}
            </optgroup>
          )}
        </select>
        
        {/* Aviso de perfil do servidor */}
        {!isLocalProfile && (
          <div className="mt-2 px-3 py-2 bg-blue-900/30 border border-blue-700/50 rounded text-[11px] text-blue-300">
            ℹ️ Perfil do servidor. Você pode editar os valores para esta execução. Salve como novo perfil local para persistir.
          </div>
        )}
        
        {/* Ações do perfil */}
        <div className="flex gap-2 mt-2">
          <input
            type="text"
            placeholder="Nome do novo perfil"
            value={newProfileName}
            onChange={e => onNewProfileNameChange(e.target.value)}
            className="flex-1 bg-navy-900 border border-navy-600 rounded px-2 py-1.5 text-xs text-gray-200 focus:border-blue-500 focus:outline-none"
          />
          <button
            onClick={onSaveProfile}
            className="flex items-center gap-1 px-2 py-1.5 bg-green-600 hover:bg-green-700 text-white text-xs rounded transition-colors"
            title="Salvar como novo perfil local"
          >
            <Plus className="w-3 h-3" />
            Salvar
          </button>
          {isLocalProfile && (
            <button
              onClick={() => onDeleteProfile(selectedProfileName)}
              className="p-1.5 bg-red-600 hover:bg-red-700 text-white rounded transition-colors"
              title="Excluir perfil local"
            >
              <Trash2 className="w-3 h-3" />
            </button>
          )}
        </div>
        
        {/* Indicador de tipo */}
        <div className="flex items-center gap-1.5 mt-2 text-[10px]">
          {isLocalProfile ? (
            <>
              <HardDrive className="w-3 h-3 text-amber-400" />
              <span className="text-amber-400">Perfil local (editável)</span>
            </>
          ) : (
            <>
              <Cloud className="w-3 h-3 text-blue-400" />
              <span className="text-blue-400">Perfil do servidor (somente leitura)</span>
            </>
          )}
        </div>
        {activeProfiles.length === 0 && (
          <div className="mt-2 px-3 py-2 bg-amber-900/30 border border-amber-700/50 rounded text-[11px] text-amber-300">
            Nenhum perfil ativo disponível. Ative ao menos um preset compartilhado no admin ou use um perfil local habilitado.
          </div>
        )}
      </div>

      {/* Material */}
      <div>
        <label className="block text-xs text-gray-400 mb-2 uppercase tracking-wide">
          Material (mm)
        </label>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-[10px] text-gray-500 mb-1">Largura</label>
            <input
              type="number"
              value={micronsToMm(editingProfile.materialWith)}
              onChange={e => onUpdateProfile({ materialWith: mmToMicrons(Number(e.target.value)) })}
              className="w-full bg-navy-900 border border-navy-600 rounded px-2.5 py-1.5 text-sm text-gray-200 focus:border-blue-500 focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-[10px] text-gray-500 mb-1">Altura</label>
            <input
              type="number"
              value={micronsToMm(editingProfile.materialHeight)}
              onChange={e => onUpdateProfile({ materialHeight: mmToMicrons(Number(e.target.value)) })}
              className="w-full bg-navy-900 border border-navy-600 rounded px-2.5 py-1.5 text-sm text-gray-200 focus:border-blue-500 focus:outline-none"
            />
          </div>
        </div>
      </div>

      {/* Espaçamento */}
      <div>
        <label className="block text-[10px] text-gray-500 mb-1">
          Espaçamento entre objetos (mm)
        </label>
        <input
          type="number"
          step="0.1"
          value={micronsToMm(editingProfile.otstup)}
          onChange={e => onUpdateProfile({ otstup: mmToMicrons(Number(e.target.value)) })}
          className="w-full bg-navy-900 border border-navy-600 rounded px-2.5 py-1.5 text-sm text-gray-200 focus:border-blue-500 focus:outline-none"
        />
      </div>

      {/* Margens */}
      <div>
        <label className="block text-xs text-gray-400 mb-2 uppercase tracking-wide">
          Margens (mm)
        </label>
        <div className="grid grid-cols-4 gap-2">
          {MARGIN_FIELDS.map(({ key, label }) => (
            <div key={key}>
              <label className="block text-[10px] text-gray-500 mb-1 text-center">
                {label}
              </label>
              <input
                type="number"
                value={micronsToMm(editingProfile[key as keyof MontaTechProfile] as number)}
                onChange={e => onUpdateProfile({ [key]: mmToMicrons(Number(e.target.value)) })}
                className="w-full bg-navy-900 border border-navy-600 rounded px-2 py-1.5 text-sm text-gray-200 text-center focus:border-blue-500 focus:outline-none"
              />
            </div>
          ))}
        </div>
      </div>

      {/* Passo Giratório */}
      <div>
        <label className="block text-xs text-gray-400 mb-2 uppercase tracking-wide">
          Passo Giratório
        </label>
        <div className="grid grid-cols-5 gap-1.5">
          {ROTATION_OPTIONS.map(({ value, label }) => (
            <button
              key={value}
              onClick={() => onUpdateProfile({ gradusCount: value as 1 | 2 | 4 | 8 | 32 })}
              className={`py-2 rounded-lg text-sm font-medium transition-colors ${
                editingProfile.gradusCount === value
                  ? 'bg-blue-600 text-white'
                  : 'bg-navy-700 text-gray-300 hover:bg-navy-600'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
    </>
  );
}

// ==================== OPTIONS TAB ====================

interface OptionsTabProps {
  editingProfile: MontaTechProfile;
  isLocalProfile: boolean;
  onUpdateProfile: (updates: Partial<MontaTechProfile>) => void;
}

function OptionsTab({
  editingProfile,
  isLocalProfile,
  onUpdateProfile
}: OptionsTabProps) {
  return (
    <>
      {/* Opções de Encaixe */}
      <div>
        <label className="block text-xs text-gray-400 mb-3 uppercase tracking-wide">
          Opções de Encaixe
        </label>
        <div className="space-y-2">
          {PROFILE_BOOLEAN_OPTIONS.encaixe.map(({ key, label }) => (
            <label key={key} className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
              <input
                type="checkbox"
                checked={editingProfile[key as keyof MontaTechProfile] as boolean}
                onChange={e => onUpdateProfile({ [key]: e.target.checked })}
                className="w-4 h-4 rounded border-navy-600 bg-navy-900 text-blue-600 focus:ring-blue-500 focus:ring-offset-0"
              />
              {label}
            </label>
          ))}
        </div>
      </div>

      {/* Opções de Visualização */}
      <div>
        <label className="block text-xs text-gray-400 mb-3 uppercase tracking-wide">
          Visualização
        </label>
        <div className="space-y-2">
          {PROFILE_BOOLEAN_OPTIONS.visualizacao.map(({ key, label }) => (
            <label key={key} className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
              <input
                type="checkbox"
                checked={editingProfile[key as keyof MontaTechProfile] as boolean}
                onChange={e => onUpdateProfile({ [key]: e.target.checked })}
                className="w-4 h-4 rounded border-navy-600 bg-navy-900 text-blue-600 focus:ring-blue-500 focus:ring-offset-0"
              />
              {label}
            </label>
          ))}
        </div>
      </div>

      {/* Área da Plotter */}
      <div>
        <label className="block text-xs text-gray-400 mb-2 uppercase tracking-wide">
          Área da Plotter (mm)
        </label>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-[10px] text-gray-500 mb-1">Largura máx.</label>
            <input
              type="number"
              value={micronsToMm(editingProfile.plotterSheetWith)}
              onChange={e => onUpdateProfile({ plotterSheetWith: mmToMicrons(Number(e.target.value)) })}
              className="w-full bg-navy-900 border border-navy-600 rounded px-2.5 py-1.5 text-sm text-gray-200 focus:border-blue-500 focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-[10px] text-gray-500 mb-1">Altura máx.</label>
            <input
              type="number"
              value={micronsToMm(editingProfile.plotterSheetHeight)}
              onChange={e => onUpdateProfile({ plotterSheetHeight: mmToMicrons(Number(e.target.value)) })}
              className="w-full bg-navy-900 border border-navy-600 rounded px-2.5 py-1.5 text-sm text-gray-200 focus:border-blue-500 focus:outline-none"
            />
          </div>
        </div>
      </div>

      {/* Layout de Grid (Blocos por Linha) */}
      <div>
        <label className="block text-xs text-gray-400 mb-2 uppercase tracking-wide">
          Layout de Grid
        </label>
        <label className="block text-[10px] text-gray-500 mb-2">
          Blocos por linha no preview e ao aplicar
        </label>
        <div className="grid grid-cols-5 gap-1.5">
          {BLOCKS_PER_ROW_OPTIONS.map(({ value, label }) => (
            <button
              key={value}
              onClick={() => onUpdateProfile({ blocksPerRow: value })}
              className={`py-2 rounded-lg text-sm font-medium transition-colors ${
                (editingProfile.blocksPerRow || 3) === value
                  ? 'bg-blue-600 text-white'
                  : 'bg-navy-700 text-gray-300 hover:bg-navy-600'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        <p className="mt-2 text-[10px] text-gray-500">
          Define quantos blocos de material serão exibidos lado a lado
        </p>
      </div>
    </>
  );
}

